import { useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOptionalRouteCatalog } from '@features/navigation/components/RouteCatalogContext'
import type { RouteNode } from '@features/navigation/logic/routeCatalog'
import type { SidebarRecentPage, SidebarRecentResponse } from '../api/contracts'
import { sidebarQueryKeys } from '../api/queryKeys'
import { getRecentPages, recordRecentVisit } from '../api/recent.api'
import { normalizeFavoritePath, pageFavoritePath } from '../logic/favorites'
import { recentSearchResult, type SidebarSearchResult } from '../logic/sidebarSearch'
import { useDescribedPaths } from './useDescribedPaths'

const RECENT_STALE_MS = 60_000
const NO_RESULTS: SidebarSearchResult[] = []

/** The places the user opened lately, newest first. */
export function useRecentPagesQuery() {
  return useQuery({
    queryKey: sidebarQueryKeys.recent,
    queryFn: getRecentPages,
    staleTime: RECENT_STALE_MS,
  })
}

/**
 * The place a visit to `pathname` counts for: the page a header's heart
 * would favorite (a table from any of its views, an automation from any of
 * its tabs), or null for an address that isn't a page of its own.
 */
export function recentPathFor(root: RouteNode, pathname: string): string | null {
  const path = pageFavoritePath(root, pathname)
  return path === null ? null : normalizeFavoritePath(path)
}

function putFirst(pages: readonly SidebarRecentPage[], path: string): SidebarRecentPage[] {
  return [
    { path, visited_at: new Date().toISOString() },
    ...pages.filter((page) => page.path !== path),
  ]
}

/**
 * Puts each place the user opens first among their recent visits, for the
 * sidebar's search: at once in the app, then on the server. A visit that
 * fails to save is let go quietly: the list is a convenience. Mounted once,
 * by the sidebar.
 */
export function useRecordRecentVisits() {
  const { pathname } = useLocation()
  const root = useOptionalRouteCatalog()
  const queryClient = useQueryClient()
  const recordedRef = useRef<string | null>(null)
  const latestVisitRef = useRef(0)
  const { mutate } = useMutation<SidebarRecentResponse, Error, string, { visit: number }>({
    mutationFn: recordRecentVisit,
    // Saved one at a time, in the order they were made, so the server's list
    // ends in that order too.
    scope: { id: 'sidebar-recent-visits' },
    onMutate: async (path) => {
      const visit = ++latestVisitRef.current
      await queryClient.cancelQueries({ queryKey: sidebarQueryKeys.recent })
      const previous = queryClient.getQueryData<SidebarRecentResponse>(sidebarQueryKeys.recent)
      queryClient.setQueryData<SidebarRecentResponse>(sidebarQueryKeys.recent, {
        recent: putFirst(previous?.recent ?? [], path),
        updated_at: previous?.updated_at ?? null,
      })
      return { visit }
    },
    onSuccess: (data, _path, context) => {
      // A later visit is still saving: this answer would put an older place first.
      if (context?.visit !== latestVisitRef.current) return
      queryClient.setQueryData(sidebarQueryKeys.recent, data)
    },
  })

  useEffect(() => {
    if (!root) return
    const path = recentPathFor(root, pathname)
    if (path === null || recordedRef.current === path) return
    recordedRef.current = path
    mutate(path)
  }, [mutate, pathname, root])
}

/**
 * The places the user opened lately as the sidebar's search shows them (each
 * named from its page's metadata, as a favorite is), newest first, and each
 * address's place among them (newest 0), for ranking matches. Places gone
 * or switched off are left out.
 */
export function useRecentResults(): {
  results: SidebarSearchResult[]
  recency: ReadonlyMap<string, number>
} {
  const { data } = useRecentPagesQuery()
  const paths = useMemo(() => data?.recent.map((page) => page.path) ?? null, [data])
  const described = useDescribedPaths(paths)
  const results = useMemo(
    () =>
      described
        ? described.flatMap((description) => recentSearchResult(description) ?? [])
        : NO_RESULTS,
    [described]
  )
  const recency = useMemo(() => new Map((paths ?? []).map((path, index) => [path, index])), [paths])
  return { results, recency }
}
